import React from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useTheme } from '../context/ThemeContext';
import { Trash2, ShoppingBag, Heart, ArrowRight, ChevronLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO'; 

const Wishlist = () => {
    const { wishlistItems, toggleWishlist, addToCart } = useCart();
    const { isDarkMode } = useTheme();

    return (
        <div className={`min-h-screen pt-32 pb-20 transition-colors duration-500 ${isDarkMode ? 'bg-[#050505] text-white' : 'bg-white text-black'}`}>
            
            <SEO 
                title="Your Wishlist | The Archive" 
                description="Secure your curated selection of rare thrifted gems. Your personal vault of future acquisitions."
                url="/wishlist"
            />

            <div className="max-w-7xl mx-auto px-6">
                
                <Link to="/products" className={`inline-flex items-center gap-2 mb-12 text-[10px] uppercase font-black tracking-[0.3em] transition-all ${isDarkMode ? 'text-zinc-500 hover:text-white' : 'text-zinc-400 hover:text-black'}`}>
                    <ChevronLeft size={14} /> Back_to_Market
                </Link>

                <div className={`flex flex-col md:flex-row md:items-end justify-between gap-6 border-b pb-12 mb-12 ${isDarkMode ? 'border-white/10' : 'border-black/10'}`}>
                    <div>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-[2px] bg-emerald-500"></div>
                            <span className="text-emerald-500 font-black text-[10px] tracking-[0.4em] uppercase">Private_Vault</span>
                        </div>
                        <h1 className="text-6xl md:text-8xl font-black uppercase italic tracking-tighter leading-none">
                            Saved <span className={isDarkMode ? 'text-zinc-800' : 'text-zinc-200'}>Items</span>
                        </h1>
                    </div>
                    <div className={`px-6 py-3 rounded-2xl border ${isDarkMode ? 'bg-zinc-900/50 border-white/5' : 'bg-zinc-100 border-black/5'}`}>
                        <p className="text-zinc-500 font-black uppercase text-[10px] tracking-[0.3em]">
                            Vault_Capacity: <span className="text-white">{wishlistItems.length} Units</span>
                        </p>
                    </div>
                </div>

                {wishlistItems.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                        {wishlistItems.map((item, index) => (
                            <motion.div 
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                key={item._id} 
                                className={`relative flex flex-col rounded-[2.5rem] p-5 border group transition-all duration-500 ${isDarkMode ? 'bg-zinc-900/20 border-white/5 hover:border-emerald-500/30' : 'bg-zinc-50 border-black/5 shadow-sm'}`}
                            >
                                <div className={`relative aspect-[3/4] rounded-[2rem] overflow-hidden border ${isDarkMode ? 'bg-black border-white/10' : 'bg-zinc-200 border-black/10'}`}>
                                    <Link to={`/product/${item._id}`} className="block w-full h-full">
                                        <img 
                                            src={item.img || item.image} 
                                            // 🔥 Alt text optimized for SEO
                                            alt={`${item.brand || 'Vintage'} ${item.name} | Authentic Thrifted Asset`} 
                                            // ⚡ Lazy loading for performance
                                            loading="lazy"
                                            className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-110 transition-all duration-1000" 
                                            crossOrigin="anonymous"
                                        />
                                    </Link>
                                    
                                    <button 
                                        onClick={() => toggleWishlist(item)}
                                        className="absolute top-4 right-4 bg-black/60 backdrop-blur-xl p-3 rounded-2xl text-red-500 border border-white/10 hover:bg-red-500 hover:text-white transition-all active:scale-90"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                                
                                <div className="mt-6 flex-1 flex flex-col">
                                    <div className="flex justify-between items-start mb-4">
                                        <div>
                                            <p className="text-[9px] font-black text-emerald-500 uppercase tracking-[0.3em] mb-1">{item.brand}</p>
                                            <Link 
                                                to={`/product/${item._id}`} 
                                                className="text-xl font-black uppercase italic tracking-tight leading-tight block hover:text-emerald-500 transition-colors"
                                            >
                                                {item.name}
                                            </Link>
                                        </div>
                                        <p className="text-xl font-black italic tracking-tighter">₹{item.price}</p>
                                    </div>
                                    
                                    <div className="flex gap-2 mt-auto">
                                        <button 
                                            onClick={() => addToCart(item, "M")}
                                            className={`flex-1 flex items-center justify-center gap-3 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all ${isDarkMode ? 'bg-white text-black' : 'bg-black text-white'}`}
                                        >
                                            <ShoppingBag size={14} /> Move_to_Bag
                                        </button>
                                        <Link 
                                            to={`/product/${item._id}`}
                                            className={`p-4 rounded-2xl border flex items-center justify-center transition-all ${isDarkMode ? 'border-white/10 hover:bg-white/5' : 'border-black/10 hover:bg-black/5'}`}
                                        >
                                            <ArrowRight size={16} />
                                        </Link>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                ) : (
                    <div className={`flex flex-col items-center justify-center py-32 rounded-[4rem] border-2 border-dashed transition-all ${isDarkMode ? 'bg-zinc-950/50 border-white/5' : 'bg-zinc-50 border-black/5'}`}>
                        <div className="relative mb-8">
                            <Heart size={80} className={`${isDarkMode ? 'text-zinc-800' : 'text-zinc-200'}`} />
                            <div className={`absolute inset-0 blur-3xl opacity-20 rounded-full ${isDarkMode ? 'bg-emerald-500' : 'bg-black'}`}></div>
                        </div>
                        <h2 className="text-4xl font-black uppercase italic tracking-tighter mb-4">Vault_Empty</h2>
                        <Link to="/products">
                            <button className={`px-12 py-5 rounded-full font-black uppercase tracking-[0.3em] text-[10px] transition-all ${isDarkMode ? 'bg-emerald-500 text-black' : 'bg-black text-white'}`}>
                                Access_Catalog
                            </button>
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Wishlist;